import moment from "moment"

export class Constants {
    static storagePrefix = "@gyroo-client-"

    static urls = {
        base: "https://api.gyroo.com.br",
        socket: "wss://api.gyroo.com.br"
    }

    static storage = {
        tokens: this.storagePrefix + "tokens",
        user: this.storagePrefix + "user",
        register: this.storagePrefix + "register-data",
        step: this.storagePrefix + "register-step"
    }

    static screenMaxWidth = window.screen.availWidth < 800 ? window.screen.availWidth : 412

    static creditEvolution = [
        500,
        1000,
        1500,
        2000,
        2500,
        3000,
        4000,
        5000,
        6000,
        8000,
        12000,
        20000
    ]

    static activityDeployedAt = moment([2025, 7, 15])

}

export class Validators {
    static validateCPF(cpf: string): boolean {
        cpf = cpf.replace(/\D/g, '');
        if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) {
            return false;
        }
        let sum = 0;
        for (let i = 0; i < 9; i++) {
            sum += parseInt(cpf.charAt(i)) * (10 - i);
        }
        let remainder = sum % 11;
        let digit1 = remainder < 2 ? 0 : 11 - remainder;

        sum = 0;
        for (let i = 0; i < 10; i++) {
            sum += parseInt(cpf.charAt(i)) * (11 - i);
        }
        remainder = sum % 11;
        let digit2 = remainder < 2 ? 0 : 11 - remainder;
        return parseInt(cpf.charAt(9)) === digit1 && parseInt(cpf.charAt(10)) === digit2;
    }

    static validateWhatsAppNumber(phoneNumber: string): boolean {
        const cleanedNumber = phoneNumber.replace(/\D/g, '');
        if (cleanedNumber.length < 10) {
            return false;
        }
        const regex = /^([14689][0-9]|2[12478]|3([1-5]|[7-8])|5([13-5])|7[193-7])9[0-9]{8}$/;
        return regex.test(cleanedNumber);
    }

    static validateCNPJ(cnpj: string) {
        cnpj = cnpj.replace(/[^\d]/g, '');

        if (cnpj.length !== 14) {
            return false;
        }

        const firstDigitWeights = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
        const secondDigitWeights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

        const calculateVerificationDigit = (cnpj: string, weights: number[]) => {
            let sum = 0;
            for (let i = 0; i < weights.length; i++) {
                sum += parseInt(cnpj.charAt(i)) * weights[i];
            }
            const remainder = sum % 11;
            return remainder < 2 ? 0 : 11 - remainder;
        };

        const firstVerificationDigit = calculateVerificationDigit(cnpj.substring(0, 12), firstDigitWeights);
        const secondVerificationDigit = calculateVerificationDigit(cnpj.substring(0, 12) + firstVerificationDigit, secondDigitWeights);

        if (
            parseInt(cnpj.charAt(12)) !== firstVerificationDigit ||
            parseInt(cnpj.charAt(13)) !== secondVerificationDigit
        ) {
            return false;
        }

        return true;
    }

    static validateEmail(email: string): boolean {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    }
}

export class Utils {
    static onlyDigits(value: string) {
        return value?.replace(/\D/g, '')
    }

    static async wait(seconds: number) {
        return await new Promise((resolve) => {
            setTimeout(() => {
                resolve(true)
            }, seconds * 1000)
        })
    }
}


export class Format {
    static currency(value: number) {
        return value?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    }

    static currencyWithoutDecimals(value: number): string {
        return value?.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })
    }

    static currencyWritten(value: number): string {
        if (value >= 2000 && value % 1000 === 0) {
            return `R$ ${value / 1000} mil`;
        }
        return this.currencyWithoutDecimals(value)
    }

    static formatCPF(cpf: string): string {
        if (!cpf) return '';
        return cpf.replace(/\D/g, '')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
    }

    static formatCNPJ(cnpj: string): string {
        if (!cnpj) return '';
        return cnpj.replace(/\D/g, '')
            .replace(/(\d{2})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,4})/, '$1/$2')
            .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
    }

    static whatsAppMessage(message: string): string {
        if (!message) return '';
        return message.replaceAll(" ", "%20").replaceAll("\n", "%0A")
    }

    static whatsAppNumber(number: string): string {
        if (!number) return ''

        const digits = number.replace(/\D/g, '')

        if (digits.length === 11) {
            return digits.replace(/(\d{2})(\d{1})(\d{4})(\d{4})/, '($1) $2 $3-$4')
        }

        if (digits.length === 10) {
            return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
        }

        return number
    }

    static dateWithTime(date?: string) {
        if (!date) return "-"
        return moment(date).format("L [às] HH:mm")
    }

    static date(date?: string) {
        if (!date) return "-"
        return moment(date).format("L")
    }
}

export const sendToShow = (id: string, placement: ScrollLogicalPosition = 'start') => {
    const element = document.getElementById(id)
    console.log(element)
    if (element) {
        element.scrollIntoView({
            block: placement ?? 'center',
            behavior: "smooth"
        });
    }
}

export const getTimeRemainingUntilCancellation = (createdAtInput: string | Date): number => {
    const createdAt = new Date(createdAtInput);

    const expirationTime = new Date(createdAt.getTime() + 10 * 60 * 1000);

    const nextCron = new Date(expirationTime);
    const minutes = nextCron.getMinutes();
    const nextCronMinutes = Math.ceil(minutes / 10) * 10;

    nextCron.setMinutes(nextCronMinutes, 0, 0);

    if (nextCronMinutes === 60) {
        nextCron.setHours(nextCron.getHours() + 1);
        nextCron.setMinutes(0);
    }

    const now = new Date();
    return Math.max(0, Math.floor((nextCron.getTime() - now.getTime()) / 1000));
}